/**
 * Minimal pub/sub state store for the library app.
 *
 * The library is multi-screen with shared state (active skin, filter picker,
 * user prefs, catalog cache). A framework would be overkill — this gives us
 * the one thing we need: selector-based subscriptions so unrelated subtrees
 * don't re-render when unrelated state changes.
 *
 * API:
 *   const state = createState(initial);
 *   state.get();                       → frozen snapshot
 *   state.set({ foo: 1 });             → shallow merge + notify
 *   state.update('foo.bar', v);        → deep path set + notify
 *   state.subscribe(select, fn);       → fn(selected) whenever select(state)
 *                                        returns a value !== Object.is to
 *                                        the previous result
 *
 * Kept intentionally small (~100 LoC). Not a framework, not a reactive
 * graph; just a notification bus that avoids manual re-render plumbing.
 */

import { DEFAULT_RELEASE_TYPES } from '../../../src/shared/release-type-vocabulary.js';

export function createState(initial = {}) {
  let current = Object.freeze({ ...initial });
  const subscribers = new Set(); // { selector, fn, lastValue }

  function get() {
    return current;
  }

  function set(partial) {
    if (!partial || typeof partial !== 'object') return;
    const next = Object.freeze({ ...current, ...partial });
    const prev = current;
    current = next;
    notify(prev);
  }

  /**
   * Deep-set via dot-path: update('prefs.default_view', 'list'). Non-existent
   * intermediate objects are created. Leaves siblings unchanged.
   */
  function update(path, value) {
    const parts = path.split('.');
    const next = { ...current };
    let cursor = next;
    for (let i = 0; i < parts.length - 1; i++) {
      const key = parts[i];
      cursor[key] = { ...(cursor[key] || {}) };
      cursor = cursor[key];
    }
    cursor[parts[parts.length - 1]] = value;
    const prev = current;
    current = Object.freeze(next);
    notify(prev);
  }

  /**
   * Subscribe to a slice of state. `selector(state)` runs on every change;
   * if its return value differs (Object.is) from the previous result, `fn`
   * fires with the new value.
   *
   * Returns an unsubscribe function.
   */
  function subscribe(selector, fn) {
    const entry = { selector, fn, lastValue: selector(current) };
    subscribers.add(entry);
    return () => subscribers.delete(entry);
  }

  function notify(prev) {
    for (const entry of subscribers) {
      const next = entry.selector(current);
      if (!Object.is(entry.lastValue, next)) {
        entry.lastValue = next;
        try {
          entry.fn(next, prev);
        } catch (err) {
          // Subscribers must not break the notification loop for their peers.
          console.error('[library/state] subscriber threw:', err);
        }
      }
    }
  }

  return { get, set, update, subscribe };
}

// Canonical default for the sidebar filter set. Exported so the browse-state
// persistence module and the initial store seed share one source of truth for
// the filter shape.
export const DEFAULT_FILTERS = {
  genre: '',          // selected genre ('' = no filter)
  tag: '',            // selected tag slug ('' = no filter)
  favorite: false,    // favorites-only toggle
  played: undefined,  // 'true' | 'false' | undefined (no filter)
  letter: '',         // alphabet quick-nav ('A'-'Z' | '#' | '')
  top100: false,      // show only Top 100 games for the active system
  filter_id: null,    // saved custom filter id
  achievements: undefined, // has-achievements toggle (truthy | undefined)
  scores: undefined,  // has-high-scores toggle (truthy | undefined)
  mine: false,        // "My ROMs" facet — owned catalog games + own uploads
  release_types: DEFAULT_RELEASE_TYPES,
};

// Singleton used by the library app. Screens and components import this
// directly rather than threading state through every function signature.
export const state = createState({
  ready: false,           // becomes true once module flag + session loaded
  user: null,             // { id, username, isAdmin } or null
  features: {},           // from /api/admin/features
  prefs: null,            // user_library_prefs row or null (anonymous)
  catalog: {
    roms: null,           // cached last /api/library/catalog response
    system: null,         // currently-selected system filter ('all' = no filter)
    search: '',
    sort: 'title',        // 'title' | 'recent' | 'favorites' | 'rating'
    view: 'grid',         // 'grid' | 'list'
    filters: { ...DEFAULT_FILTERS }, // Phase 4+ sidebar filters (see DEFAULT_FILTERS)
  },
  route: { path: '/library', params: {} },
});
